import { compact, filter, includes, some, sumBy } from 'lodash-es';
import { ApplicationConfigurationCustomQuestion } from '@zg-rentals/ts-capps-client';
import type { Answers, Document } from '../models/rental-application';
import { hasDogDetails } from './capps-mappers';
import { pluralize } from './formatters';

export const SUPPLEMENTAL_CUSTOM_QUESTIONS: ReadonlySet<ApplicationConfigurationCustomQuestion> = new Set([
  ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD,
  ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD,
  ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD,
  ApplicationConfigurationCustomQuestion.ADDITIONAL_OCCUPANTS,
  ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_INFO,
  ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_PERMISSION,
  ApplicationConfigurationCustomQuestion.LANDLORD_PHONE,
  ApplicationConfigurationCustomQuestion.LANDLORD_EMAIL,
  ApplicationConfigurationCustomQuestion.LANDLORD_CONTACT_PERMISSION,
  ApplicationConfigurationCustomQuestion.VEHICLES,
  ApplicationConfigurationCustomQuestion.PERSONAL_REFERENCE,
  ApplicationConfigurationCustomQuestion.ACCOUNTANT_CONTACT,
  ApplicationConfigurationCustomQuestion.ATTORNEY_CONTACT,
  ApplicationConfigurationCustomQuestion.BROKERAGE_CONTACT,
  ApplicationConfigurationCustomQuestion.FINANCIAL_CONTACTS_BUSINESS_ADDRESS,
  ApplicationConfigurationCustomQuestion.EMERGENCY_CONTACT,
]);

export function getIsCustomQuestionEnabled(
  answers: Answers | null | undefined,
  question: ApplicationConfigurationCustomQuestion,
) {
  return includes(answers?.enabledCustomQuestions, question);
}

export function getHasSupplementalCustomQuestions(answers: Answers | null | undefined) {
  return some(answers?.enabledCustomQuestions, (question) => SUPPLEMENTAL_CUSTOM_QUESTIONS.has(question));
}

export const CUSTOM_QUESTION_DOCUMENTS_LIMIT = 15;

export function getCustomQuestionDocuments(
  answers: Answers | null | undefined,
  question: ApplicationConfigurationCustomQuestion,
): Array<Document> {
  return answers?.customQuestionDocuments?.find((section) => section.customQuestion === question)?.documents ?? [];
}

export function getPetCounts(answers: Answers | null | undefined) {
  const pets = answers?.pets;
  const listedDogs = filter(pets?.dogs, hasDogDetails).length;
  const dogs = listedDogs || sumBy([pets?.smallDogs, pets?.mediumDogs, pets?.largeDogs], (count) => Number(count ?? 0));
  const cats = Number(pets?.cats ?? 0);
  const other = Number(pets?.other ?? 0);
  return { dogs, cats, other, total: dogs + cats + other };
}

export function getHasPets(answers: Answers | null | undefined) {
  return getPetCounts(answers).total > 0;
}

export function getPetSummaryParts(answers: Answers | null | undefined) {
  const { dogs, cats, other } = getPetCounts(answers);
  return compact([
    dogs > 0 && pluralize('dog', dogs),
    cats > 0 && pluralize('cat', cats),
    other > 0 && pluralize('other pet', other),
  ]);
}

export function getPetSummarySeparator(index: number, count: number) {
  if (index === 0) {
    return '';
  }
  if (index < count - 1) {
    return ', ';
  }
  return count > 2 ? ', and ' : ' and ';
}

export function getPetSummary(answers: Answers | null | undefined) {
  const parts = getPetSummaryParts(answers);
  return parts.map((part, index) => `${getPetSummarySeparator(index, parts.length)}${part}`).join('');
}
